<script lang="ts">
import {defineComponent, onMounted, ref} from "vue";
import {useAuthStore} from "~/plugins/store/auth";
import {translation, useTranslationStore} from "~/plugins/store/translations";
import type {TranslationList} from "~/types/Utils";
import {useNuxtApp} from "#app";
import {useUserStore} from "~/plugins/store/user";
import {useReCaptcha} from "vue-recaptcha-v3";
import LoadingDialog from "~/components/utils/LoadingDialog.vue";
import AccountLayout from "~/layouts/AccountLayout.vue";
import {type ApiVehicleDetails, createDefaultApiVehicleDetails} from "~/types/Vehicle";
import {useVehicleStore} from "~/plugins/store/vehicle";

export default defineComponent({
  components: {AccountLayout, LoadingDialog},
  setup(props) {
    definePageMeta({
      middleware: ['auth'],
    });

    useHead({
      title: 'Xiga | Masinile mele',
      meta: [
        {
          name: 'description',
          content: 'META'
        }
      ],
    });

    const translationStore = useTranslationStore();
    const translationList = ref<TranslationList>({});
    const authStore = useAuthStore();
    const user = ref(authStore.getUser());
    let accessToken = authStore.getAccessToken();
    const {$toast} = useNuxtApp();
    const userStore = useUserStore();
    const recaptchaInstance = useReCaptcha();
    const showLoadingDialog = ref(true);
    const messageLoadingDialog = ref('Se incarcă...');
    const vehicle = ref(createDefaultApiVehicleDetails() as ApiVehicleDetails | null);
    const vehicleStore = useVehicleStore();

    const route = useRoute();
    const vehicleId = parseInt(route.params.id as string, 10);


    onMounted(async () => {
      translationList.value = await translationStore.getTranslations('ro', 'dashboard');
      if (accessToken) {
        showLoadingDialog.value = true;
        messageLoadingDialog.value = 'Se încarcă...';
        vehicle.value = await vehicleStore.getVehicle(vehicleId, accessToken);
        showLoadingDialog.value = false;
      }
    });
    const getManufacturerLogo = (manufacturer: string) => {
      const imagePath = '/images/cars/' + manufacturer + '.png';
      return imageExists(imagePath) ? imagePath : '/images/cars/no-make.png';
    };

    const imageExists = (url: string) => {
      try {
        const http = new XMLHttpRequest();
        http.open('HEAD', url, false);
        http.send();
        return http.status !== 404;
      } catch (error) {
        return false;
      }
    };

    const getExpirationClass = (expirationDate: string | null | undefined) => {
      if (!expirationDate) return 'text-red';
      const now = new Date();
      const expiration = new Date(expirationDate);
      const diffTime = expiration.getTime() - now.getTime();
      const diffDays = diffTime / (1000 * 3600 * 24);

      if (diffDays < 0) {
        return 'text-red';
      } else if (diffDays < 60) {
        return 'text-warning';
      } else {
        return 'text-success';
      }
    };

    return {
      user,
      accessToken,
      translationList,
      translation,
      showLoadingDialog,
      messageLoadingDialog,
      vehicle,
      getManufacturerLogo,
      getExpirationClass,
    };
  }
});
</script>

<template>
  <account-layout class="profile">
    <v-card rounded="xl" variant="elevated" elevation="15" class="w-100 pa-5" min-height="400">
      <loading-dialog :message="messageLoadingDialog" :show-dialog="showLoadingDialog"/>
      <v-row>
        <v-col cols="12" sm="12" md="12" class="d-flex flex-column justify-start centered-on-mobile">
          <h3 class="font-weight-bold">
            {{ translation(translationList.my_profile, 'Mașina mea') }}</h3>
          <p class="font-weight-regular">
            {{
              translation(translationList.my_profile_helper, 'Aici poți vedea detaliile mașinii tale și informațiile legate de aceasta.')
            }}
          </p>
        </v-col>
      </v-row>
      <v-row v-if="vehicle && vehicle.vin">
        <v-col cols="12">
          <v-row>
            <v-col cols="12" sm="12" md="12" class="d-flex flex-column justify-start centered-on-mobile">
              <h3 class="font-weight-bold">
                {{ translation(translationList.vehicle_details, 'Detalii mașină') }}
              </h3>
            </v-col>
          </v-row>
          <v-card rounded="xl" color="gray-light" variant="flat">
            <v-row class="ma-2">
              <v-col cols="6">
                <div class="my-2">
                  {{ translation(translationList.registration_plate, 'Nr. înmatriculare') }}:
                  <span class="font-weight-bold">{{ vehicle.registration_plate }}</span>
                </div>
                <div class="my-2">
                  {{ translation(translationList.vin, 'VIN') }}:
                  <span class="font-weight-bold">{{ vehicle.vin }}</span>
                </div>
                <div class="my-2" v-if="vehicle.rca_mapped_category">
                  {{ translation(translationList.category, 'Categorie') }}:
                  <span class="font-weight-bold">{{ vehicle.rca_mapped_category.name }}</span>
                </div>
                <div class="my-2" v-if="vehicle.rca_vehicle_make">
                  {{ translation(translationList.make, 'Brand') }}:
                  <span class="font-weight-bold">{{ vehicle.rca_vehicle_make.name }}</span>
                </div>
                <div class="my-2" v-if="vehicle.rca_vehicle_model">
                  {{ translation(translationList.model, 'Model') }}:
                  <span class="font-weight-bold">{{ vehicle.rca_vehicle_model.name }}</span>
                </div>
                <div class="my-2" v-if="vehicle.manufacture_year">
                  {{ translation(translationList.manufacture_year, 'An fabricație') }}:
                  <span class="font-weight-bold">{{ vehicle.manufacture_year }}</span>
                </div>
                <div class="my-2" v-if="vehicle.vehicle_fuel_type">
                  {{ translation(translationList.fuel_type, 'Tip combustibil') }}:
                  <span class="font-weight-bold">{{ vehicle.vehicle_fuel_type.name }}</span>
                </div>
              </v-col>
              <v-col cols="6">
                <div class="my-2" v-if="vehicle.cylinder">
                  {{ translation(translationList.cylinder, 'Cilindree') }}:
                  <span class="font-weight-bold">{{ vehicle.cylinder }}</span>
                </div>
                <div class="my-2" v-if="vehicle.power">
                  {{ translation(translationList.power, 'Putere') }}:
                  <span class="font-weight-bold">{{ vehicle.power }}</span>
                </div>
                <div class="my-2" v-if="vehicle.max_weight">
                  {{ translation(translationList.max_weight, 'Greutate maximă') }}:
                  <span class="font-weight-bold">{{ vehicle.max_weight }}</span>
                </div>
                <div class="my-2" v-if="vehicle.number_of_seats">
                  {{ translation(translationList.number_of_seats, 'Număr de locuri') }}:
                  <span class="font-weight-bold">{{ vehicle.number_of_seats }}</span>
                </div>
                <div class="my-2" v-if="vehicle.civ_code">
                  {{ translation(translationList.civ_code, 'Cod CIV') }}:
                  <span class="font-weight-bold">{{ vehicle.civ_code }}</span>
                </div>
                <div class="my-2" v-if="vehicle.vehicle_usage_type">
                  {{ translation(translationList.vehicle_usage_type, 'Tip utilizare') }}:
                  <span class="font-weight-bold">{{ vehicle.vehicle_usage_type.name }}</span>
                </div>
                <div class="my-2">
                  {{ translation(translationList.is_leasing, 'Este în leasing') }}:
                  <span class="font-weight-bold">{{ vehicle.rca_leasing_company_id ? 'Da' : 'Nu' }}</span>
                </div>
                <div class="my-2" v-if="vehicle.rca_leasing_company">
                  {{ translation(translationList.leasing_company, 'Companie leasing') }}:
                  <span class="font-weight-bold">{{ vehicle.rca_leasing_company.name }}</span>
                </div>
              </v-col>
            </v-row>
          </v-card>
          <div v-if="vehicle.vehicle_owner">
            <v-row>
              <v-col cols="12" sm="12" md="12" class="d-flex flex-column justify-start centered-on-mobile">
                <h3 class="font-weight-bold">
                  {{ translation(translationList.owner_details, 'Detalii proprietar') }}
                </h3>
              </v-col>
            </v-row>
            <v-card rounded="xl" color="gray-light" variant="flat">
              <v-row class="ma-2">
                <v-col cols="6">
                  <div class="my-2">
                    {{ translation(translationList.full_name, 'Nume proprietar') }}:
                    <span class="font-weight-bold">{{
                        vehicle.vehicle_owner.is_legal ? vehicle.vehicle_owner.company_name
                            : vehicle.vehicle_owner.first_name + ' ' + vehicle.vehicle_owner.last_name
                      }}</span>
                  </div>
                  <div class="my-2" v-if="!vehicle.vehicle_owner.is_legal">
                    {{ translation(translationList.unique_code, 'CNP') }}:
                    <span class="font-weight-bold">{{ vehicle.vehicle_owner.cnp }}</span>
                  </div>
                  <div class="my-2" v-else>
                    {{ translation(translationList.unique_code, 'CUI') }}:
                    <span class="font-weight-bold">{{ vehicle.vehicle_owner.cif_code }}</span>
                  </div>
                  <div class="my-2" v-if="!vehicle.vehicle_owner.is_legal">
                    {{ translation(translationList.series, 'Serie') }}:
                    <span class="font-weight-bold">{{ vehicle.vehicle_owner.series_ci }}</span>
                  </div>
                  <div class="my-2" v-if="!vehicle.vehicle_owner.is_legal">
                    {{ translation(translationList.number, 'Număr') }}:
                    <span class="font-weight-bold">{{ vehicle.vehicle_owner.nr_ci }}</span>
                  </div>
                </v-col>
                <v-col cols="6">
                  <div class="my-2">
                    {{ translation(translationList.phone, 'Telefon') }}:
                    <span class="font-weight-bold">{{ vehicle.vehicle_owner.phone_number }}</span>
                  </div>
                  <div class="my-2">
                    {{ translation(translationList.email, 'Email') }}:
                    <span class="font-weight-bold">{{ vehicle.vehicle_owner.email }}</span>
                  </div>
                </v-col>
              </v-row>
            </v-card>
          </div>
          <div>
            <v-row>
              <v-col cols="12" sm="12" md="12" class="d-flex flex-column justify-start centered-on-mobile">
                <h3 class="font-weight-bold">
                  {{ translation(translationList.rca, 'RCA') }}
                </h3>
              </v-col>
            </v-row>
            <v-card rounded="xl" color="gray-light" variant="flat" link>
              <v-row class="ma-2">
                <v-col cols="12">
                  <div class="my-2"
                       v-if="vehicle.default_vehicle_expiration && vehicle.default_vehicle_expiration.rca_expires_at">
                    {{ translation(translationList.rca_expiration_date, 'Data expirare RCA') }}:
                    <span :class="getExpirationClass(vehicle.default_vehicle_expiration.rca_expires_at)"
                          class="font-weight-bold">{{ vehicle.default_vehicle_expiration.rca_expires_at }}</span>
                  </div>
                  <div class="my-2" v-else>
                    {{ translation(translationList.rca_expiration_date, 'Data expirare RCA') }}:
                    <span class="text-warning font-weight-bold">{{
                        translation(translationList.not_identified, 'Neidentificat')
                      }}</span>
                  </div>
                </v-col>
              </v-row>
            </v-card>
          </div>
          <div>
            <v-row>
              <v-col cols="12" sm="12" md="12" class="d-flex flex-column justify-start centered-on-mobile">
                <h3 class="font-weight-bold">
                  {{ translation(translationList.vehicle_diary, 'Jurnal Auto') }}
                </h3>
              </v-col>
            </v-row>
            <v-card rounded="xl" color="gray-light" variant="flat" link>
<!--              TODO WIP-->
<!--              @click="navigateTo('/account/vehicles/' + vehicle.id + '/diaries/')"-->
              <v-row class="ma-2">
                <v-col cols="12">
                  <div class="my-2">
                    {{
                      translation(translationList.diary_info, 'Noteaza-ti aici evenimentele relevante pentru masina ta – revizii, reparatii, consum')
                    }}
                  </div>
                </v-col>
              </v-row>
            </v-card>
          </div>
          <div>
            <v-row>
              <v-col cols="12" sm="12" md="12" class="d-flex flex-column justify-start centered-on-mobile">
                <h3 class="font-weight-bold">
                  {{ translation(translationList.vehicle_notes, 'Notite rapide') }}
                </h3>
              </v-col>
            </v-row>
            <v-card rounded="xl" color="gray-light" variant="flat" link>
<!--              TODO WIP-->
<!--              @click="navigateTo('/account/vehicles/' + vehicle.id + '/notes/')"-->
              <v-row class="ma-2">
                <v-col cols="12">
                  <div class="my-2">
                    {{
                      translation(translationList.diary_info, 'Noteaza-ti aici informatii pe care nu vrei sa le uiti – marimea caucicurilor, data de expirare ITP')
                    }}
                  </div>
                </v-col>
              </v-row>
            </v-card>
          </div>
        </v-col>
      </v-row>
      <v-skeleton-loader v-else type="image"></v-skeleton-loader>
    </v-card>
  </account-layout>
</template>

<style scoped>

</style>
